import React from 'react';
import './Process.css';
import IconBadge from './IconBadge';
import SectionCTA from './SectionCTA';

const steps = [
  {
    num: '01',
    title: 'Audit & Discover',
    desc: 'We map your workflow, identify efficiency drag, and spot AI-ready opportunities.',
    checks: ['System check', 'Process check', 'Speed check', 'Manual work', 'Repetitive task'],
    ui: (
      <div className="process-ui audit-ui">
        <div className="audit-title">Analyzing current workflow</div>
        <div className="audit-checks">
          {['System check', 'Process check', 'Speed check', 'Manual work', 'Repetitive task'].map((c, i) => (
            <div key={i} className="audit-check">
              <IconBadge name="audit" size="sm" className="audit-check-icon" />
              <span>{c}</span>
              <div className="audit-check-bar">
                <div
                  className="audit-check-fill"
                  style={{ width: `${60 + i * 8}%`, animationDelay: `${i * 0.12}s, ${1.3 + i * 0.12}s` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    num: '02',
    title: 'Automation Buildout',
    desc: 'After digitalization, we build the automation layer that removes repetitive work across your operation.',
    ui: (
      <div className="process-ui code-ui">
          <div className="code-header">
            <div className="code-dots"><span/><span/><span/></div>
          <span className="code-filename">automation.py</span>
        </div>
        <pre className="code-block">
<span className="tok-kw">class</span> <span className="tok-cls">AutomationTrigger</span>:{'\n'}
{'  '}<span className="tok-kw">def</span> <span className="tok-fn">__init__</span>(<span className="tok-self">self</span>, threshold):{'\n'}
{'    '}<span className="tok-self">self</span>.threshold = threshold{'\n'}
{'    '}<span className="tok-self">self</span>.status = <span className="tok-str">"inactive"</span>{'\n'}
{'\n'}
{'  '}<span className="tok-kw">def</span> <span className="tok-fn">check_trigger</span>(<span className="tok-self">self</span>, value):{'\n'}
{'    '}<span className="tok-kw">if</span> value &gt; <span className="tok-self">self</span>.threshold:{'\n'}
{'      '}<span className="tok-self">self</span>.status = <span className="tok-str">"active"</span>{'\n'}
{'      '}<span className="tok-kw">return</span> <span className="tok-str">"Automation triggered!"</span>{'\n'}
{'    '}<span className="tok-kw">return</span> <span className="tok-str">"No action taken."</span>{'\n'}
{'\n'}
{'  '}<span className="tok-kw">def</span> <span className="tok-fn">get_status</span>(<span className="tok-self">self</span>):{'\n'}
{'    '}<span className="tok-kw">return</span> <span className="tok-str">f"Status: {'{'}</span><span className="tok-self">self</span>.status<span className="tok-str">{'}'}"</span>
        </pre>
      </div>
    ),
  },
  {
    num: '03',
    title: 'AI Integration',
    desc: 'Once automation is stable, we integrate AI into the right workflows with minimal disruption.',
    ui: (
      <div className="process-ui integration-ui">
        <div className="integration-cols">
          <div className="int-col">
            <div className="int-label">Our solution</div>
            <div className="int-icons">
              {['automation', 'ai', 'insights', 'integration'].map((icon, i) => (
                <div key={i} className="int-icon"><IconBadge name={icon} size="md" /></div>
              ))}
            </div>
          </div>
          <div className="int-arrows">
            <svg className="int-arrow-right" width="40" height="16" viewBox="0 0 40 16" fill="none">
              <path d="M0 8H36M30 2L38 8L30 14" stroke="#5ECBA1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <svg className="int-arrow-left" width="40" height="16" viewBox="0 0 40 16" fill="none">
              <path d="M40 8H4M10 2L2 8L10 14" stroke="#5ECBA1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="int-col">
            <div className="int-label">Your stack</div>
            <div className="int-icons">
              {['workflow', 'support', 'search', 'productivity'].map((icon, i) => (
                <div key={i} className="int-icon"><IconBadge name={icon} size="md" /></div>
              ))}
            </div>
          </div>
        </div>
        <div className="integration-status">Connected & syncing</div>
      </div>
    ),
  },
  {
    num: '04',
    title: 'Continuous Optimization',
    desc: 'We refine performance, analyze insights, and enhance automation for long-term growth.',
    ui: (
      <div className="process-ui optimization-ui">
        {[
          { name: 'Chatbot system', status: 'Efficiency will increase by 20%', color: '#5ECBA1' },
          { name: 'Workflow system', status: 'Update available..', color: '#fbbf24' },
          { name: 'Sales system', status: 'Up to date', color: '#60a5fa' },
        ].map((item, i) => (
          <div key={i} className="opt-item">
            <div className="opt-dot" style={{ background: item.color }} />
            <div>
              <div className="opt-name">{item.name}</div>
              <div className="opt-status" style={{ color: item.color }}>{item.status}</div>
            </div>
          </div>
        ))}
      </div>
    ),
  },
];

// Marks each step .active as it crosses 35% of the viewport so the
// number badge and connector light up in sequence while scrolling.
function useStepActivation() {
  React.useEffect(() => {
    const steps = document.querySelectorAll('.process-step');
    if (!steps.length) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) e.target.classList.add('active');
      }),
      { threshold: 0.35 }
    );
    steps.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);
}

export default function Process() {
  useStepActivation();
  return (
    <section className="process" id="process">
      <div className="process-inner">
        <div className="process-header">
          <span className="section-label">Our Process</span>
          <h2 className="section-title">Our Process</h2>
          <p className="process-sub">Our delivery path is straightforward: digitalize the workflow, automate the bottlenecks, then integrate AI where it compounds outcomes.</p>
        </div>
        <div className="process-steps">
          {steps.map((step, i) => (
            <div className="process-step" key={i}>
              <div className="step-left">
                <div className="step-num">{step.num}</div>
                <div className="step-connector" />
              </div>
              <div className="step-right">
                <div className="step-content">
                  <h3 className="step-title">Step {step.num.replace('0','')}: {step.title}</h3>
                  <p className="step-desc">{step.desc}</p>
                </div>
                <div className="step-ui-wrap">{step.ui}</div>
              </div>
            </div>
          ))}
        </div>
        <SectionCTA label="Ready to map your own automation process?" />
      </div>
    </section>
  );
}
